import * as React from "react";
import { CalendarBlank } from "@phosphor-icons/react";
import { pt } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MONTHS_PT } from "@/utils/dateFmt";
import { cn } from "@/lib/utils";

const BASE = "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

const toIso = (txt) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(txt);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  if (d.getFullYear() !== Number(m[3]) || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[1])) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
};
const toText = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
const mask = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  return d.length > 4 ? `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}` : d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

/** Campo de data dd/mm/aaaa com calendário; emite {target:{value:'AAAA-MM-DD'}} como um input nativo. */
export const DateField = ({ value, onChange, min, max, className, disabled, name, placeholder, required, id, ...rest }) => {
  const [text, setText] = React.useState(toText(value));
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { if ((toIso(text) || "") !== (value || "")) setText(toText(value)); /* eslint-disable-next-line */ }, [value]);
  const invalid = text !== "" && !(toIso(text) && (!min || toIso(text) >= min) && (!max || toIso(text) <= max));
  const emit = (v) => onChange && onChange({ target: { value: v, name }, currentTarget: { value: v, name } });
  const type = (e) => {
    const t = mask(e.target.value);
    setText(t);
    if (t === "") emit("");
    else { const iso = toIso(t); if (iso && (!min || iso >= min) && (!max || iso <= max)) emit(iso); else if (value) emit(""); }
  };
  const sel = value && /^\d{4}-\d{2}-\d{2}/.test(value) ? new Date(`${value.slice(0, 10)}T12:00:00`) : undefined;
  return (
    <div className={cn("relative", className && /\bw-/.test(className) ? "" : "w-full")}>
      <input type="text" inputMode="numeric" autoComplete="off" id={id} name={name} value={text} onChange={type} disabled={disabled} required={required}
        placeholder={placeholder || "dd/mm/aaaa"} maxLength={10} aria-invalid={invalid || undefined} className={cn(BASE, "pr-9", invalid && "border-red-400 focus-visible:ring-red-400", className)} {...rest} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Abrir calendário" className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-green" data-testid={rest["data-testid"] ? `${rest["data-testid"]}-calendar` : undefined}><CalendarBlank size={16} /></button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[100]" align="start">
          <Calendar mode="single" locale={pt} selected={sel} defaultMonth={sel} captionLayout="dropdown-buttons" fromYear={1920} toYear={2099} classNames={{ vhidden: "hidden", caption_label: "hidden", caption_dropdowns: "flex gap-1 items-center justify-center", dropdown: "h-7 rounded-md border border-input bg-white px-1 text-xs", dropdown_month: "", dropdown_year: "", caption: "flex justify-center pt-1 relative items-center px-7" }}
            onSelect={d => { if (!d) return; const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; setText(toText(iso)); emit(iso); setOpen(false); }} />
        </PopoverContent>
      </Popover>
    </div>
  );
};

/** Seletor de mês por extenso ("novembro 2026"); emite 'AAAA-MM'. */
export const MonthField = ({ value, onChange, className, disabled, name, min, max, ...rest }) => {
  const now = new Date();
  const m = /^(\d{4})-(\d{2})/.exec(value || "");
  const year = m ? Number(m[1]) : now.getFullYear();
  const month = m ? Number(m[2]) : now.getMonth() + 1;
  const emit = (y, mo) => onChange && onChange({ target: { value: `${y}-${String(mo).padStart(2, "0")}`, name }, currentTarget: { value: `${y}-${String(mo).padStart(2, "0")}`, name } });
  const years = Array.from({ length: 16 }, (_, i) => 2020 + i);
  const sel = "h-9 rounded-md border border-input bg-transparent px-2 text-sm shadow-sm disabled:opacity-50";
  return (
    <div className={cn("inline-flex gap-1.5", className)} data-testid={rest["data-testid"]}>
      <select className={sel} value={month} disabled={disabled} onChange={e => emit(year, Number(e.target.value))} aria-label="Mês" data-testid={rest["data-testid"] ? `${rest["data-testid"]}-month` : undefined}>
        {MONTHS_PT.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
      </select>
      <select className={sel} value={year} disabled={disabled} onChange={e => emit(Number(e.target.value), month)} aria-label="Ano" data-testid={rest["data-testid"] ? `${rest["data-testid"]}-year` : undefined}>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
};
